'use client';

import React, { forwardRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const surfaces = [
{
  id: 'driveways',
  label: 'Residential',
  title: 'Driveways & Concrete',
  psi: '3,200 PSI',
  time: '45 min avg',
  cost: '$0.15–0.30/sq ft',
  recovery: '+$8,400',
  recoveryLabel: 'avg value added at sale',
  description:
  'Oil stains, tire marks, and moss growth on concrete and pavers. A single pass at 3,200 PSI with a surface cleaner removes 18 months of grime — the kind of transformation neighbors photograph from the street.',
  details: ['Concrete & stamped concrete', 'Exposed aggregate', 'Brick & cobblestone pavers', 'Asphalt sealing prep'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11736cd29-1764672714247.png",
  imageAlt: 'Clean residential driveway after pressure washing, smooth gray concrete',
  accent: 'var(--accent-blue)'
},
{
  id: 'storefronts',
  label: 'Commercial',
  title: 'Windows',
  psi: '1,500 PSI',
  time: '2–4 hrs avg',
  cost: '$0.12–0.22/sq ft', // Windows: 1,500 PSI confirmed
  recovery: '+$22,000',
  recoveryLabel: 'avg lease value per clean',
  description:
  'Grime, hard water deposits, and streaks on commercial and residential windows. Crystal-clear windows at 1,500 PSI with soft-wash technique restore visibility and curb appeal — restaurant owners report increased foot traffic after a professional window clean.',
  details: ['Storefront glass panels', 'Residential windows', 'Skylights & awnings', 'Screen & frame cleaning'],
  image: "https://images.unsplash.com/photo-1651361666964-275902f89603",
  imageAlt: 'Commercial windows being cleaned, sparkling glass facade',
  accent: '#2A3744'
},
{
  id: 'fleet',
  label: 'Fleet & Municipal',
  title: 'Fleet & Vehicle Bays',
  psi: '1,500 PSI',
  time: '20–40 min/vehicle',
  cost: '$35–85/vehicle', // Fleet & Vehicle Bays: 1,500 PSI confirmed
  recovery: '+$1,200',
  recoveryLabel: 'per vehicle residual value',
  description:
  'Heavy-equipment degreasing, fleet washing programs, and municipal infrastructure. Property managers with 60-unit complexes use our fleet program to turn over parking structures in a single July afternoon.',
  details: ['Commercial trucks & vans', 'Construction equipment', 'Parking deck surfaces', 'Municipal infrastructure'],
  image: "https://images.unsplash.com/photo-1697252312641-561fba5494c8",
  imageAlt: 'Fleet vehicle being pressure washed in industrial bay',
  accent: 'var(--accent-blue)'
},
{
  id: 'trashcans',
  label: 'Sanitation',
  title: 'Trash Cans',
  psi: '3,000 PSI',
  time: '5–15 min/can',
  cost: '$15–20/can',
  recovery: '+$800',
  recoveryLabel: 'avg odor & pest prevention value',
  description:
  'Bacteria, mold, and odor buildup inside residential and commercial trash cans. A high-pressure hot-water rinse eliminates 99% of surface bacteria and removes stubborn residue — keeping pests away and properties smelling clean.',
  details: ['Residential bins', 'Commercial dumpster exteriors', 'Recycling containers', 'HOA common-area cans'],
  image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64",
  imageAlt: 'Clean trash cans lined up after pressure washing service',
  accent: 'var(--accent-blue)'
}];


const SurfaceBreakdown = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section
      ref={ref}
      id="surfaces"
      className="reveal-section"
      style={{
        background: 'var(--arctic-white)',
        padding: 'clamp(4rem, 8vw, 7rem) clamp(2rem, 6vw, 6rem)',
        borderTop: '1px solid var(--mist-gray)'
      }}>
      
      {/* Section header */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-4">
          <div style={{ width: '32px', height: '2px', background: 'var(--accent-blue)' }} />
          <span
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--accent-blue)',
              fontFamily: 'Plus Jakarta Sans, sans-serif'
            }}>
            
            § 03 — Surface Types
          </span>
        </div>
        <h2
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: 'var(--slate)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            maxWidth: '560px'
          }}>
          
          Every surface has a{' '}
          <span style={{ color: 'var(--accent-blue)' }}>different number.</span>
        </h2>
        <p
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '1rem',
            color: 'var(--mist-gray)',
            lineHeight: 1.65,
            maxWidth: '480px',
            marginTop: '1rem'
          }}>
          
          Residential driveways, commercial windows, and fleet vehicles each carry distinct ROI profiles. Here is what the data shows for each category.
        </p>
      </div>
      {/* Surface cards */}
      <div className="space-y-6">
        {surfaces?.map((surface, i) =>
        <div
          key={surface?.id}
          className="surface-card"
          style={{ animationDelay: `${i * 0.1}s` }}>
          
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Image */}
              <div
              className="lg:col-span-4 relative overflow-hidden"
              style={{ minHeight: '260px' }}>
              
                <AppImage
                src={surface?.image}
                alt={surface?.imageAlt}
                fill
                className="object-cover"
                style={{ transition: 'transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)' }} />
              
                {/* Label overlay */}
                <div
                className="absolute top-4 left-4"
                style={{
                  background: 'rgba(42,55,68,0.85)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '6px',
                  padding: '0.375rem 0.75rem'
                }}>
                
                  <span
                  style={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: 'var(--mist-gray)',
                    fontFamily: 'Plus Jakarta Sans, sans-serif'
                  }}>
                  
                    {surface?.label}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div
              className="lg:col-span-8"
              style={{ padding: '2rem 2rem 2rem 2.5rem' }}>
              
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <h3
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      color: 'var(--slate)',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                      marginBottom: '0.5rem'
                    }}>
                    
                      {surface?.title}
                    </h3>
                    <p
                    style={{
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '0.875rem',
                      color: 'var(--mist-gray)',
                      lineHeight: 1.6,
                      maxWidth: '480px'
                    }}>
                    
                      {surface?.description}
                    </p>
                  </div>

                  {/* Recovery callout */}
                  <div
                  style={{
                    background: 'rgba(0,119,204,0.06)',
                    border: '1px solid rgba(0,119,204,0.15)',
                    borderRadius: '10px',
                    padding: '1rem 1.25rem',
                    textAlign: 'center',
                    flexShrink: 0,
                    minWidth: '140px'
                  }}>
                  
                    <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.75rem',
                      color: 'var(--accent-blue)',
                      letterSpacing: '-0.03em',
                      lineHeight: 1
                    }}>
                    
                      {surface?.recovery}
                    </div>
                    <div
                    style={{
                      fontSize: '0.6875rem',
                      color: 'var(--mist-gray)',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      marginTop: '0.25rem',
                      lineHeight: 1.3
                    }}>
                    
                      {surface?.recoveryLabel}
                    </div>
                  </div>
                </div>

                {/* Specs row */}
                <div
                className="flex flex-wrap gap-6 mb-4"
                style={{ borderTop: '1px solid var(--mist-gray)', paddingTop: '1rem', marginTop: '1rem' }}>
                
                  {[
                { label: 'Pressure', value: surface?.psi },
                { label: 'Service Time', value: surface?.time },
                { label: 'Cost Range', value: surface?.cost }]?.
                map((spec) =>
                <div key={spec?.label}>
                      <div
                    style={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--mist-gray)',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      marginBottom: '0.25rem'
                    }}>
                    
                        {spec?.label}
                      </div>
                      <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      color: 'var(--slate)'
                    }}>
                    
                        {spec?.value}
                      </div>
                    </div>
                )}
                </div>

                {/* Detail list */}
                <div className="flex flex-wrap gap-2">
                  {surface?.details?.map((detail) =>
                <span
                  key={detail}
                  style={{
                    background: 'var(--arctic-white)',
                    border: '1px solid var(--mist-gray)',
                    borderRadius: '4px',
                    padding: '0.25rem 0.625rem',
                    fontSize: '0.75rem',
                    color: 'var(--slate)',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontWeight: 500
                  }}>
                  
                      {detail}
                    </span>
                )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>);

});

SurfaceBreakdown.displayName = 'SurfaceBreakdown';
export default SurfaceBreakdown;